import React from 'react';
import { ref } from 'valtio';
import * as Pioneer from 'pioneer';
import './app.css';
import { WMTSComponent, SceneHelpers, CelestialGridComponent } from 'pioneer-scripts';

import { withRouter } from 'react-router-dom';
import { dateToUTCString, getDateFromUTCDateStr, isEmptyObj, getDateFromLocalDateStr, localDateToNoonUTC } from './helpers/tools';

// Import css
import '../www/assets/css/style.css';
import '../www/assets/css/layout.css';
import '../www/assets/css/labels.css';
import '../www/assets/css/vital_signs.css';
import '../www/assets/css/branding.css';
import '../www/assets/css/template_mobile.css';

import ContentManager from './managers/content_manager';
import TimeManager from './managers/time_manager';
import SceneManager from './managers/scene_manager';
import VideoManager from './managers/video_manager';
import CameraManager from './managers/camera_manager';
import LabelManager from './managers/label_manager';
import RouteManager from './managers/route_manager';
import DatasetManager from './managers/dataset_manager';
import ComparisonManager from './managers/comparison_manager';
import VisibleEarthManager from './managers/visible_earth_manager';
import AppVersion from './app_version';
import { eventsStore, refsStore, lightStore, kioskStore, datasetStore, spacecraftStore, layersStore, poiStore, gmapStore } from './managers/globalState';
import UILayout from './components/ui-layout';
import SplashScreen from './components/splash_screen';
import { setColorRectStyle } from './components/color_bar';
import { setReadoutData } from './components/data_readout';
import SPACECRAFT_DATA from './data/spacecraft_data';
import SPACECRAFT_DATA_AR from '../www/assets/data/spacecraft_data_ar';

import ar from './languages/ar';
import en from './languages/en';
//import { STATIC_ASSETS_URL, DYNAMIC_ASSETS_URL, POI_CATEGORY_INDEX, EVENTS_CATEGORY_INDEX } from './config/constants'; //where is this done now??
import { Config } from './config/config';

window.CelestialGridComponent = CelestialGridComponent;

class App extends React.PureComponent {
	/**
	 * Constructs the app and initializes app-level variables
	 * @param {any} props
	 */
	constructor(props) {
		super(props);

		this.state = {
			sceneIsReady: false,
			validQueries: false,
			isMobile: this.isMobile(),
			isWindows: this.isWindows(),
			is2k: this.is2k(),
			is4k: this.is4k(),
			isFullscreen: false,
			isLandscape: this.isLandscape(),
			view: 'vitalsign',
			viewID: '',
			viewIDPoI: '',
			viewIDGmap: '',
			previousView: null,
			spacecraft: '',
			vitalsign: 'visible_earth',
			altid: 0,
			altView: 0,
			animating: 'f',
			showOnGlobe: 'f',
			start: '',
			end: '',
			hideUI: false
		};

		// Refs
		/**
		 * The React reference to the Pioneer div.
		 * @type {React.RefObject<HTMLDivElement>}
		 * @private
		 */
		this._pioneerRef = React.createRef();
		this._vitalSignsRef = React.createRef();
		this._datasetToolsRef = React.createRef();
		this._datePickerRef = React.createRef();
		this._controlsRef = React.createRef();
		this._dateLabelRef = React.createRef();
		this._dateRangeRef = React.createRef();
		this._missionsOverlayRef = React.createRef();
		this._datasetsOverlayRef = React.createRef();
		this._timeRateControlsRef = React.createRef();
		this._detailOverlayRef = React.createRef();
		this._loadIconRef = React.createRef();

		// Binds
		this._resize = this._resize.bind(this);
		this.get = this.get.bind(this);
		this.isMobile = this.isMobile.bind(this);
		this.is2k = this.is2k.bind(this);
		this.is4k = this.is4k.bind(this);
		this.goToVitalView = this.goToVitalView.bind(this);
		this.goToMissionView = this.goToMissionView.bind(this);
		this.changeAltView = this.changeAltView.bind(this);
		this._eventClicked = this._eventClicked.bind(this);
		this._poiClicked = this._poiClicked.bind(this);
		this._gmapClicked = this._gmapClicked.bind(this);
		this.setFullscreen = this.setFullscreen.bind(this);
		this.getCursorPosition = this.getCursorPosition.bind(this);

		/**
		 * The Pioneer engine.
		 * @type {Pioneer.Engine}
		 * @private
		 */
		this._pioneer = null;

		/**
		 * The Pioneer main scene.
		 * @type {Pioneer.Scene}
		 * @private
		 */
		this._scene = null;

		/**
		 * Earth-pioneer content
		 * @type ContentManager
		 * @private
		 */
		this._content = null;

		/**
		 * Boolean for when mounting has complete.
		 * @type {boolean}
		 * @private
		 */
		this._hasMounted = false;

		/**
		 * If parseUrl is calleed prematurely (before component has finished mounting),
		 * we can store the href here to call once the mounting has complete.
		 * @type {string}
		 * @private
		 */
		this._parseUrlCallbackArg = null;

		// Managers
		this._managerTypes = {
			content: ContentManager,
			time: TimeManager,
			label: LabelManager,
			scene: SceneManager,
			video: VideoManager,
			visibleEarth: VisibleEarthManager,
			camera: CameraManager,
			route: RouteManager,
			dataset: DatasetManager,
			comparison: ComparisonManager
		};
		this._managers = {};

		// Stores if the label was clicked to prevent conflict with select controller
		this._labelClicked = false;

		this._cursorPosition = new Pioneer.Vector2(-200, -200);

		// Touch vs mouse behavior
		this._hasMouse = matchMedia('(pointer:fine)').matches;
		if (this._hasMouse) { document.body.classList.remove('touch'); }

		this._touchStartPos = {
			x: 0,
			y: 0
		};
		this._dragging = false;

		window.addEventListener('orientationchange', this._resize);

		window.addEventListener('resize', () => {
			this._resize();
			const { is4k, is2k, isWindows } = this.state;

			const newState = {};

			const isMobile = this.isMobile();
			if (isMobile !== this.state.isMobile) {
				newState.isMobile = isMobile;
			}

			const isWindowsOS = this.isWindows();
			if (isWindowsOS !== isWindows) {
				newState.isWindows = isWindowsOS;
			}

			const is2kRes = this.is2k();
			if (is2kRes !== is2k) {
				newState.is2k = is2kRes;
			}

			const is4kRes = this.is4k();
			if (is4kRes !== is4k) {
				newState.is4k = is4kRes;
			}

			const isLandscape = this.isLandscape();
			if (isLandscape !== this.state.isLandscape) {
				newState.isLandscape = isLandscape;
			}

			this.setState(newState);

			// Scroll back at the top
			if (isMobile) {
				window.scroll(0, 0);
			}
		});
		this._config = window.config ? window.config : {};

    this.language = ar;
	}

	async componentDidMount() {
		// Create global references for the app and the Pioneer namespace.
		window.app = this;
		window.Pioneer = Pioneer;

		// Load the config.
		await Config.load();

		// Create Pioneer.
		this._pioneer = new Pioneer.Engine(this._pioneerRef.current);
		this._pioneer.registerComponentType('wmts', WMTSComponent);

		// Set the global refs.
		refsStore.setGlobalState({
			get: ref(this.get),
			pioneer: ref(this._pioneer)
		});

		// Subscribe to vital sign global store.
		window.datasetStore = datasetStore;

		this._pioneer
			.getDownloader()
			.setReplacement('STATIC_ASSETS_URL', Config.staticAssetsUrl);
		this._pioneer
			.getDownloader()
			.setReplacement('DYNAMIC_ASSETS_URL', Config.dynamicAssetsUrl);

		// Create managers that doesn't need scene
		await this.createPreManagers();

		// Create the scene and its entities.
		/**
		 * Todo: On first load, with a fresh cache, createScene takes ~10 seconds to execute.
		 * With everything cached, it takes 2 seconds. This is better, but means nothing if you lost all the users on the first load...
		 * We should look to reduce this load time as much as possible (and give the user more info, eg. load %, what it's loading etc...) - See ticket #3194
		 */
		await this.createScene();
		// Create managers that need scene
		await this.createPostManagers();

		/**
		 * Set global state callbacks.
		 */

		// Zoom in / out callback.
		spacecraftStore.subscribeTo('isZoomedOut', isZoomedOut => {
			const { spacecraftName } = spacecraftStore.stateSnapshot;

			const targetEntityName = isZoomedOut ? 'earth' : spacecraftName;
			const targetEntity = targetEntityName && this._scene.get(targetEntityName);

			// Return if entity doesn't exist.
			if (!targetEntity) { return; }

			// Grace trails turned on when zoomed out.
			if (spacecraftName.includes('sc_grace_fo')) {
				const gfo1 = this._scene.get('sc_grace_fo1');
				const gfo2 = this._scene.get('sc_grace_fo2');
				gfo1.getComponentByType('trail').setEnabled(isZoomedOut);
				gfo2.getComponentByType('trail').setEnabled(isZoomedOut);
			}
		});

		// Since the app is fullscreen, this removes the ability for zooming in and out on the webpage.
		// If this is an iframe, anything outside the iframe will not be affected.
		// Add touch class if needed. Todo: make this dynamic.
		if (this.isTouch()) {
			document.body.classList.add('touch');
		}

		this._touchCount = 0;
		this._scrollable = false;
		this._touchMax = false;
		window.addEventListener('touchstart', event => {
			this._touchCount++;
			this._touchMax = this._touchCount > 1;
			this._labelClicked = false;
			const touchEvent = event.changedTouches[0];
			this._touchStartPos.x = touchEvent.pageX;
			this._touchStartPos.y = touchEvent.pageY;
			this._dragging = false;

			// Check if element is scrollable or child of scrollable
			this._scrollable = false;
			let element = event.target;
			while (element.parentElement !== null) {
				if (element.classList.contains('scrollable')) {
					this._scrollable = true;
					break;
				}
				element = element.parentElement;
			}
		}, false);


		window.addEventListener('touchmove', event => {
			const touchEvent = event.changedTouches[0];
			const pressedPositionDistance = Math.max(
				Math.abs(touchEvent.pageX - this._touchStartPos.x),
				Math.abs(touchEvent.pageY - this._touchStartPos.y)
			);
			if (pressedPositionDistance > 5) {
				this._dragging = true;
				// Clear data readout and colorbar cursor on drag.
        if(this._dataReadoutRef){
          this._dataReadoutRef.current.setValueAndPosition('NaN', new Pioneer.Vector2(-200, -200));
        }
				if (this._colorBarRef) {
					this._colorBarRef.current.setValuePosition('NaN');
				}
			} else {
				// For data reading
				this._cursorPosition.x = event.pageX;
				this._cursorPosition.y = event.pageY;
			}

			// Prevent pinch zoom on the page
			if (!this._scrollable) {
				event.preventDefault();
			}
		}, { passive: false });

		window.addEventListener('touchend', event => {
			this._touchCount--;
			if (event.touches.length === 0) {
				this._touchMax = false;
			}
			this._dragging = false;
			this._scrollable = false;
		});

		// Get cursor position for data readout display.
		window.addEventListener('click', event => {
			this._cursorPosition.x = event.pageX;
			this._cursorPosition.y = event.pageY;
			console.log('xy',this._cursorPosition.x, this._cursorPosition.y )
			setReadoutData(null);
			setColorRectStyle(null);
		});

		// Listen to camera update event
		window.addEventListener('loading', () => {
			this._loadIconRef.current?.classList.remove('hidden');
		});

		// Listen to camera update event
		// TODO: move to loadicon component - See ticket #3187
		window.addEventListener('allloaded', () => {
			this._loadIconRef.current?.classList.add('hidden');
		});

		// Hydrate the vital sign data (currently takes ~100ms)
		await this.get('content')?.hydrateVitalData();

		// Wait one frame for the camera's aspect ratio, and thusly vertical field-of-view, to be set.
		await this._pioneer.waitUntilNextFrame();

		// Set the ready state.
		this.setState({ sceneIsReady: true }, () => window.dispatchEvent(new Event('resize')));

		/**
		 * ToDo: create task to separate splash screen into it's own component and use, with loading, as a fallback (after update to React 18)
		 * ToDo: fix following getElements and potential timeout memory leaks
		 */
		// document.getElementById('splash-screen').style.opacity = '0';
		// document.getElementById('splash-screen').style.filter = 'brightness(0)';
		// setTimeout(() => {
		// 	document.getElementById('splash-screen').remove();
		// }, 1000);

		// Set hasMounted to true.
		this._hasMounted = true;

		// parseUrl callbacks called if they exist.
		if (this._parseUrlCallbackArg) {
			this.parseUrl(this._parseUrlCallbackArg).catch(err => {
				if (err) {
					console.error(err);
				}
			}).then(() => { this.updateParentUrl(); });

			// Reset callback arg.
			this._parseUrlCallbackArg = null;
		}
	}

	static getDerivedStateFromProps(nextProps, state) {
		let { location: { hash, search }, match: { params: { view, viewID, viewIDPoI, viewIDGmap } } } = nextProps;
		// In case search is empty, check hash
		if (!search) {
			search = hash;
		}
		// In case view and viewID are undefined, check hash
		const pathname = hash.split('?')[0];
		if (pathname) {
			const viewParts = pathname.split('/');
			viewParts.shift();
			if (!view) {
				view = viewParts[0];
			}
			if (viewParts.length > 1 && !viewID) {
				viewID = viewParts[1];
			}
		}
		const props = RouteManager.parseQuery(search);
		props.view = view || 'vitalsign';
		props.viewID = viewID;
		props.viewIDPoI = viewIDPoI;
		props.viewIDGmap = viewIDGmap;

		if (!('altid' in props)) {
			props.altid = 0;
		}
		if (!('altView' in props)) {
			props.altView = 0;
		}
		if (!('vitalsign' in props)) {
			props.vitalsign = 'visible_earth';
		}
		if (!('spacecraft' in props)) {
			props.spacecraft = '';
		}
		if (['spacecraft'].includes(view)) {
			props.vitalsign = 'satellites';
		}
		if (!('animating' in props)) {
			props.animating = 'f';
		}
		if (!('showOnGlobe' in props)) {
			props.showOnGlobe = 'f';
		}
		if (!('start' in props)) {
			props.start = '';
		}
		if (!('end' in props)) {
			props.end = '';
		}
		if (!('videoId' in props)) {
			props.videoId = null;
		}

		let didAnythingChange = false;
		const newState = Object.entries(props).reduce((obj, [key, value]) => {
			if (state[key] !== props[key]) {
				obj[key] = value;
				didAnythingChange = true;
			}
			return obj;
		}, {});
		if (didAnythingChange) {
			// Make sure to only set these if something did in fact change
			// i.e. this function runs on resize events
			newState.previousView = state.view;
			newState.oldvitalsign = state.vitalsign;
			newState.oldaltid = state.altid;
		}
		return newState;
	}

	componentDidUpdate(prevProps, prevState) {
		const keys = Object.keys(prevState);
		for (let i = keys.length - 1; i >= 0; i--) {
			const key = keys[i];
			if (key === 'internal') {
				continue;
			}
			if (prevState[key] !== this.state[key] && this.state[key] !== 'event') {
				if (this._hasMounted) {
					this.parseUrl(window.location.href).catch(err => {
						if (err) {
							console.error(err);
						}
					}).then(() => { this.updateParentUrl(); });
				} else {
					// Store the href callback arg to call once mounted.
					this._parseUrlCallbackArg = window.location.href;
				}
				break;
			}
		}
	}

	_resize() {
		// Force UI height
		const ui = document.querySelector('.ui');
		if (ui) {
			ui.style.height = window.innerHeight;
			document.querySelector('.app').style.height = window.innerHeight;
		}
	}

	updateParentUrl() {
		window.parent.postMessage(`{"message": "setState", "url": "${window.location.hash}"}`, '*');
	}

	validateParams({ vitalsign, spacecraft, altid, altView, animating, start, end }) {
		const { view } = this.state;
		const contentManager = this.get('content');
		const vitalData = contentManager.vitalList.find(vital => vital.value === vitalsign);
		const satellites = vitalsign === 'satellites';


		// Vital sign or spacecraft query validation.
		if (!vitalData || (spacecraft && !this._scene.get(spacecraft))) {
			return {
				valid: false,
				redirect: ['/']
			};
		}

		// - vital sign is satellites and altView is not 0
		// - vital sign is visible_earth, altid is 0, and altView is not 0
		if (
			(satellites || (vitalsign === 'visible_earth' && altid === 0))
			&& altView !== 0
		) {
			return {
				valid: false,
				redirect: [{ altView: 0 }]
			};
		}

		if (!satellites) {
			// - other vital signs
			const newParams = {};
			// - overflow altid
			if (vitalData.datasetGroups[altid] === undefined) {
				newParams.altid = 0;
			}
			// - overflow altView
			if (vitalData.datasetGroups?.[altid]?.datasets[altView] === undefined) {
				newParams.altView = 0;
			}
			// - validate start and end dates for animation
			if (animating === 't' && start && end) {
				const { min, max } = this.get('time').dateLimits;

				const startDateLocal = getDateFromLocalDateStr(start);
				const endDateLocal = getDateFromLocalDateStr(end);

				/**
				 * When comparing date values, it's important to make sure we're consistent.
				 * Here we're making sure the date limits and the entered start and end queries
				 * are both convert to UTC noon before being compared.
				 */
				const startDateVal = localDateToNoonUTC(startDateLocal).valueOf();
				const endDateVal = localDateToNoonUTC(endDateLocal).valueOf();

				const invalidStart = isNaN(startDateVal);
				const invalidEnd = isNaN(endDateVal);
				const beforeMin = startDateVal < localDateToNoonUTC(min).valueOf();
				const afterMax = endDateVal > localDateToNoonUTC(max).valueOf();

				if (invalidStart || invalidEnd) {
					newParams.start = '';
					newParams.end = '';
					newParams.animating = 'f';
				} else if (beforeMin || afterMax) {
					if (beforeMin) {
						newParams.start = dateToUTCString(min);
					}
					if (afterMax) {
						newParams.end = dateToUTCString(max);
					}
				}
			}
			if (!isEmptyObj(newParams)) {
				return {
					valid: false,
					redirect: [newParams, view, true]
				};
			}
		}

		return { valid: true };
	}

	/**
	 * TODO: Proper routing required.
	 * This method is often called before componentDidMount has even finished.
	 * @param {string} url
	 */

	async parseUrl(url) {
		const allQueries = [
			'vitalsign',
			'spacecraft',
			'altView',
			'altid',
			'videoId',
			'showOnGlobe',
			'animating',
			'start',
			'end',
			'hideUI',
			'trailWidth',
			'kiosk',
			'autoplay'
		];
		const urlQueries = RouteManager.parseQuery(url);
		if (this.get('route')) {
			this.get('route').query = urlQueries;
		}
		const { view, viewID, validQueries } = this.state;

		const params = Object.fromEntries(allQueries.map(key => {
			// eslint-disable-next-line react/destructuring-assignment
			const value = urlQueries[key] ?? this.state[key];
			return [key, value];
		}));

		const {
			animating,
			start,
			end,
			showOnGlobe,
			spacecraft,
			vitalsign,
			videoId,
			altid,
			altView,
			hideUI,
			trailWidth,
			kiosk,
			autoplay
		} = params;

		const datasetManager = this.get('dataset');
		const contentManager = this.get('content');
		const videoManager = this.get('video');
		const sceneManager = this.get('scene');
		const camManager = this.get('camera');


		const { valid, redirect } = this.validateParams(params);
		const validChanging = !valid || validQueries !== valid;

		this.setState({ validQueries: valid });

		// This catches the first validity check and allows us to return early as we know parseUrl will be called again.
		if (validChanging) {
			if (!valid) {
				this.get('route').navigate(...redirect);
			}
			return;
		}

		const kioskModeOn = kiosk === true || Config.kiosk === true;
		const playAutoplay = typeof (autoplay) === 'boolean' ? autoplay : kioskModeOn;
		kioskStore.setGlobalState({ kioskModeOn, kioskUrl: kiosk, playAutoplay });

		// Set videoId
		videoManager?.setCurrentVideoId(videoId);
		const videoType = videoManager?.getVideoType(videoId);

		// Remove left shift from pioneer viewport if not viewing a modal video or a chart.
		if (videoType !== 'modal' && videoType !== 'static') {
			camManager?.offsetPioneerViewportLeft(0);
		}

		// Set vital sign.
		contentManager.setVitalSign(vitalsign);

		// Set dataset.
		datasetManager.setDataset({ altid, altView });

		// Load vital sign in the scene.
		sceneManager.loadVitalSign();
		sceneManager.trailWidth = trailWidth;

		// Reset readout data on globe so UI doesn't persist when changing URL
		setReadoutData(null);

		// Set dataset booleans and animation props.
		datasetManager.setDatasetBools({ start, end, animating });
		datasetManager.setAnimationProps({ startDateStr: start, endDateStr: end });

		// init/hide visible earth textures.
		const { isVisibleEarth } = datasetManager;
		const { isSatellitesNow } = contentManager;
		if ((isVisibleEarth || isSatellitesNow) && videoType !== 'global') {
			await this.visibleManager.init();
		} else {
			this.visibleManager.destroy();
		}

		// Initialize the animation.
		datasetManager.initAnimation();


		// Show/hide latest event placemarks
		const showLatest = (view === 'vitalsign' || !view) && (isVisibleEarth || isSatellitesNow) && hideUI !== true;
		eventsStore.setGlobalState({ showLatest });
		poiStore.setGlobalState({ showLatestPoI: showLatest });
		gmapStore.setGlobalState({ showLatestGmap: showLatest });

		// Show/hide all event placemarks
		const showEventsOnGlobe = showOnGlobe === 't' && hideUI !== true && view !== 'spacecraft';
		// OK to use eventStore.setGlobalState for showOnGlobe here as we are parsing the url.
		eventsStore.setGlobalState({ showOnGlobe: showEventsOnGlobe });

		const earth = this._scene.getEntity('earth');

    //reset poiInterval if exists
    if(window.poiInterval){
      clearInterval(window.poiInterval)
    }

	 //reset gmapInterval if exists
	 if(window.gmapInterval){
		clearInterval(window.gmapInterval)
	  }

		if (view === 'spacecraft' && spacecraft) {
			earth.getComponent('patch').setEnabled(false);
			// turn on source and destination s/c for camera transition
			if (this._scene.get('camera').getParent() !== null) {
				sceneManager.showOnlySC([this._scene.get('camera').getParent().getName(), spacecraft]);
			} else {
				sceneManager.showOnlySC([spacecraft]);
			}
			// Set global state values
			lightStore.setGlobalState({ fill: true });

			this._scLoading = true;

			await camManager.goToEntity(spacecraft);

			// only leave the new s/c enabled
			sceneManager.showOnlySC([spacecraft], true);
			this._scLoading = false;
		} else if (view === 'event') {
			// Set light global state to true.
			lightStore.setGlobalState({ fill: true });

			await this._eventClicked(viewID);
		} else if (view === 'poi') {
			// Set light global state to true.
			lightStore.setGlobalState({ fill: true });

			await this._poiClicked(viewID);
		} else if (view === 'gmap') {
			// Set light global state to true.
			lightStore.setGlobalState({ fill: true });

			await this._gmapClicked(viewID);
		} else {
			earth.getComponent('patch').setEnabled(false);
			if (isSatellitesNow) {
				// Set light global state to false.
				lightStore.set;
				GlobalState({ fill: false });

				this.get('time').setRealTime();
			} else {
				// Set light global state to true.
				lightStore.setGlobalState({ fill: true });
			}

			await camManager.goToEntity(
				'earth',
				{
					latitude: 2.75,
					longitude: 29
				}
			).then(() => {
				const { dataset } = datasetManager;

				// If not on satellites or VIIRS visible earth, show only relevant missions
				if (!isSatellitesNow && !isVisibleEarth) {
					const { missionIds } = dataset || {};
					if (missionIds) {
						// Show only s/c, destroy previous s/c only once new entity has loaded
						sceneManager.showOnlySC([...missionIds], true);
					}
				}
			});
		}

		spacecraftStore.setGlobalState({
			spacecraftName: view !== 'event' && spacecraft ? spacecraft : null,
			isZoomedOut: spacecraft === 'earth'
		});

		// Trigger load video if videoId is defined.
		if (videoId) {
			videoManager?.loadVideo(videoId);
		}

		// Hide all but specified UI elements when hideUI flag is in the URL
		if (hideUI !== null) {
			const uiElements = document.getElementById('UI').getElementsByTagName('*');

			// Safety counters to avoid infinite loop
			let safetyCounter = 0;
			const maxSafetyValue = 1000;

			for (let i = 0; i < uiElements.length; i++) {
				if (typeof (uiElements[i].className) === 'string' && !uiElements[i].className?.includes('showUI')) {
					let parent = uiElements[i];
					let showElement = false;

					while (parent !== null && safetyCounter++ < maxSafetyValue) {
						parent = parent.parentElement;
						if (parent?.className?.includes('showUI')) {
							showElement = true;
							break;
						}
					}
					// if these are opacity 1 in their style the loading animation doesn't work
					if (uiElements[i].className?.includes('load-icon')) {
						return;
					}

					!showElement && (uiElements[i].style.opacity = (hideUI === true ? 0 : 1));
				}
			}
		}
	}

	async createPreManagers() {
		const { history } = this.props;

		this._content = this._addManager('content', 'content', this._pioneer);

		await this._content.init();
		// console.log('missionList', this._content._missionList);
		if (this._content && this._content._missionList && Array.isArray(this._content._missionList)) {
			const spacecraft_ar = (this._config.acceptLanguage && this._config.acceptLanguage == 'ar') ? SPACECRAFT_DATA_AR : SPACECRAFT_DATA;
			for (const key in spacecraft_ar) {
				let exists = false;
				for (let i = 0; i < this._content._missionList.length; i++) {
					if (this._content._missionList[i].external_id === key) {
						exists = true;
					}
				}
				if (!exists) {
					this._content._missionList.push(spacecraft_ar[key]);
				}
			}
		}

		// Organise time.
		const timeManager = this._addManager('time', 'time');
		const nowLocal = new Date();
		// Set the time to now and real-time rate.

		// Set date limits.
		timeManager.setMin(new Date(Date.UTC(1970, 0, 1)));

		const todayStr = dateToUTCString(nowLocal);
		timeManager.setMax(getDateFromUTCDateStr(todayStr));

		// Set time and time rate.
		timeManager.setTime(nowLocal);
		// set default rate time to 1 sec/sec instead of 30 sec/sec - Zahrah
		timeManager.setTimeRate(1);

		// Add route manager.
		const router = this._addManager('route', 'route');
		router.history = history;
	}

	createPostManagers() {
		// Create label manager
		this._addManager('label', 'label', this._scene._entities, this._pioneer);
		this._managers.label.setAlgorithm(LabelManager.Quadtree);
		this._pioneer.addCallback(() => {
			this._managers.label.update();
		});

		// create visible earth manager
		this.visibleManager = this._addManager('visibleEarth', 'visibleEarth', this);

		// Add trail manager
		// this._addManager('trail', 'trail', this._scene);

		const camera = this._addManager('camera', 'camera', this, this._scene, this._pioneer);
		camera.setUnselectableEntities(['sun', 'earth', 'moon']);

		this._addManager('dataset', 'dataset', this);

		// Add the video manager
		this._addManager('video', 'video', this);
	}

	_addManager(name, type, ...args) {
		const TypeConstructor = this._managerTypes[type];

		if (this._managers[name] === undefined) {
			this._managers[name] = new TypeConstructor(...args);
		}
		return this._managers[name];
	}

	// Creates the scene.
	// ToDo: This should be done in the scene manager.
	async createScene() {
		this._scene = this._pioneer.addScene('main');

		// Add scene manager
		const sceneManager = this._addManager('scene', 'scene', this._pioneer, this._scene, this.get('route'), this.get);
		sceneManager.init();
		sceneManager.createCityPlacemarks();

		// Create the viewport and camera.
		this.createViewportAndCamera();
		const cameraEntity = this._scene.get('camera');
		const camera = cameraEntity.getComponentByType('camera');
		if (Config.spout && Config.spoutGlobe) {
			this._scene.setAmbientLightColor(new Pioneer.Color(1, 1, 1));
		} else {
			cameraEntity.addComponent('lightSource');
		}

		this._addManager('comparison', 'comparison', this._pioneer, this._scene, this.get);

		// Populate labels and dynamic environment map.
		const dynEnvMap = /** @type {Pioneer.DynamicEnvironmentMapComponent} */ (this._scene.get('camera').addComponent('dynEnvMap'));
		for (let i = 0, l = this._scene.getNumEntities(); i < l; i++) {
			const entity = this._scene.getEntity(i);
			const divComponent = entity.get('div');

			if (divComponent !== null) {
				// Set active camera
				divComponent.setActiveCamera(camera);
				const div = divComponent.getDiv();
				const className = this._content.getClassName(entity.getName());
				div.className += ' no-select ' + className;
				if (this._content.isClickable(entity.getName())) {
					div.className += ' ' + 'clickable';
				}
				const icon = document.createElement('span');
				icon.className = 'icon';
				const span = document.createElement('span');
				span.className = 'text';
				// console.log(entity._name)
				if (entity._name.indexOf('sc_') == 0) {
					if (this.language[entity._name]) {
						span.innerHTML = this.language[entity._name];
					} else {
						console.error('Missing spacecraft name: ', entity._name);
						span.innerHTML = div.innerHTML;
					}
				} else {
					span.innerHTML = div.innerHTML;
				}
				div.innerHTML = '';
				div.appendChild(icon);
				div.appendChild(span);

				window.addEventListener('mousedown', event => {
					this._labelClicked = false;
				}, false);
				const entityName = entity.getName();

				if (this._content.isClickable(entityName)) {
					div.addEventListener('click', async event => {
						const navigated = await this.get('route').navigate({
							spacecraft: entityName,
							vitalsign: 'satellites',
							__remove: ['altid', 'animating', 'start', 'end']
						}, 'spacecraft');
						this._labelClicked = true;
						if (!navigated && !this._scLoading) {
							this._zoomRef.current.toggle();
						}
						event.preventDefault();
					}, false);
					div.addEventListener('touchend', event => {
            const entityName = entity.getName();

						if (!this._dragging && event.touches.length === 0 && !this._touchMax) {

							const navigated = this.get('route').navigate({
								spacecraft: entityName,
								vitalsign: 'satellites',
								__remove: ['altid', 'animating', 'start', 'end']
							}, 'spacecraft');
							this._labelClicked = true;
							if (!navigated) {
								this.get('camera').goToEntity(entityName);
							}
							event.preventDefault();
						}
					}, false);
					div.addEventListener('touchend', event => {
						if (!this._dragging && event.touches.length === 0 && !this._touchMax) {
							const navigated = this.get('route').navigate({
								spacecraft: entityName,
								vitalsign: 'satellites',
								__remove: ['altid', 'animating', 'start', 'end']
							}, 'spacecraft');
							this._labelClicked = true;
							if (!navigated) {
								this.get('camera').goToEntity(entityName);
							}
							event.preventDefault();
						}
					}, false);
					div.addEventListener('mouseover', event => {
						this.get('time').pause();
					});


					div.addEventListener('mouseout', event => {
						this.get('time').play();
					});


					div.addEventListener('mousemove', event => {
						event.preventDefault();
					}, true);


					div.style.cursor = 'pointer';
				}
				if (entity._name === 'sc_iss_oco_3') {
					div.className += ' ' + 'clickable';
					div.addEventListener('mouseover', event => {
						this._pioneer.get('main', 'camera', 'camera').setOutline(new Pioneer.Color(0.4, 0.85, 0.93), this._pioneer.get('main', 'sc_iss', 'model'), 'OCO-3');
					});
					div.addEventListener('mouseout', event => {
						this._pioneer.get('main', 'camera', 'camera').setOutline(new Pioneer.Color(0, 0, 0), this._pioneer.get('main', 'sc_iss', 'model'), 'OCO-3');
					});
					div.addEventListener('touchend', event => {
						if (!this._dragging && event.touches.length === 0 && !this._touchMax) {
							this.toggleOCO3HighlightColor();
							this._labelClicked = true;
							event.preventDefault();
						}
					}, false);
					div.addEventListener('click', async event => {
						await this.get('route').navigate({
							spacecraft: entity._name,
							vitalsign: 'satellites',
							__remove: ['altid', 'animating', 'start', 'end']
						}, 'spacecraft');
						this._labelClicked = true;
						event.preventDefault();
					});
				}
				const model = /** @type {Pioneer.ModelComponent} */ (entity.getComponentByType('model'));
				if (model !== null) {
					model.setDynamicEnvironmentMapComponent(dynEnvMap);
					model.setEnvironmentIntensity(0.1);
				}
			}
		}

		// If spout is enabled, enable it.
		if (Config.spout) {
			this.enableSpout(
				Config.spoutRenderWidth,
				Config.spoutGlobe,
				12000,
				'earth',
				Config.spoutFontSize,
				Config.spoutLonAngleOffset,
				Config.spoutAlignToNorthPole
			);
		}
		window.setTimeout(() => {
			['moon', 'sun'].map(v => {
				const el = document.getElementsByClassName(v)[0];
				if (el) {
					el.lastChild.innerText = this.language[v];
				}
			});
		}, 15000);

		await this._pioneer.waitUntilNextFrame();
		// Wait till everything is loaded, and then wait one more frame to make sure any positions are set.
		await this._scene.getLoadedPromise();
		await SceneHelpers.waitTillEntitiesInPlace(this._scene, new Set(['earth']));
		return this._pioneer.waitUntilNextFrame();
	}

	/**
	 * @param {number} renderWidth
	 * @param {boolean} globe
	 * @param {number} globeDistance
	 * @param {string | undefined} target
	 * @param {number} fontSize
	 * @param {number} lonAngleOffset
	 * @param {boolean} alignToNorthPole
	 */
	enableSpout(renderWidth, globe, globeDistance, target, fontSize, lonAngleOffset, alignToNorthPole) {
		// Setup the viewport as a invisible.
		const spoutViewport = this._pioneer.getViewport('spoutViewport') || this._pioneer.addViewport('spoutViewport');
		spoutViewport.getDiv().style.display = 'none';
		spoutViewport.getDiv().style.width = '100%';
		spoutViewport.getDiv().style.height = '100%';

		// Enable this to see the spout render.
		// spoutViewport.getDiv().style.display = '';
		// spoutViewport.getDiv().style.height = '50%';

		// Get the entity that will be the center for the globe spout.
		const targetEntity = globe ? this._scene.getEntity(target) : null;

		// Setup the Spout camera entity. If it's a globe, put a new entity at the target. Otherwise, just use the existing camera entity.
		let spoutCamera;
		if (globe) {
			spoutCamera = this._scene.getEntity('spoutCamera') || this._scene.addEntity('spoutCamera');
			spoutCamera.clearControllers();
			spoutCamera.setParent(targetEntity);
			spoutCamera.setPosition(Pioneer.Vector3.Zero);
			spoutCamera.setOrientation(Pioneer.Quaternion.Identity);
			const align = spoutCamera.addControllerByClass(Pioneer.AlignController);
			align.setPrimaryAlignType('align');
			align.setPrimaryAxis(Pioneer.Vector3.ZAxis);
			align.setPrimaryTargetAxis(Pioneer.Vector3.ZAxis);
			if (alignToNorthPole) {
				align.setPrimaryTargetEntity(target);
			} else {
				align.setPrimaryTargetEntity('camera');
			}
			const xAxis = new Pioneer.Vector3();
			Pioneer.Geometry.getXYZFromLLAOnSphere(xAxis, new Pioneer.LatLonAlt(0, Pioneer.MathUtils.degToRad(180 + lonAngleOffset), 1), 1);
			align.setSecondaryAlignType('align');
			align.setSecondaryAxis(Pioneer.Vector3.XAxis);
			align.setSecondaryTargetEntity('camera');
			align.setSecondaryTargetAxis(xAxis);
		} else {
			spoutCamera = this._scene.getEntity('camera');
		}

		// Add Spout component.
		const spoutComponent = spoutCamera.getComponentByClass(Pioneer.SpoutComponent) ?? spoutCamera.addComponentByClass(Pioneer.SpoutComponent);
		spoutViewport.setCamera(spoutComponent);
		spoutComponent.setRenderWidth(renderWidth);
		if (globe) {
			spoutComponent.setForGlobe(true, globeDistance || targetEntity.getExtentsRadius() * 2);
			spoutComponent.setNearDistance(targetEntity.getExtentsRadius() * 0.5);
			targetEntity.get('atmosphere')?.setExcludedFromCamera(spoutComponent, true);
		}

		// Turn divs into labels.
		for (let i = 0, l = this._scene.getNumEntities(); i < l; i++) {
			const entity = this._scene.getEntity(i);

			const divComponent = entity.get('div');
			if (divComponent instanceof Pioneer.DivComponent) {
				const label = entity.getComponentByClass(Pioneer.LabelComponent) ?? entity.addComponentByClass(Pioneer.LabelComponent);
				label.setText('⌾ ' + divComponent.getDiv().querySelector('.text').innerHTML);
				label.setColor(new Pioneer.Color(1, 1, 1, 1));
				label.setFontSize(fontSize);
				label.setAlignment(new Pioneer.Vector2(0, 0.5));
				label.setPixelOffset(new Pioneer.Vector2(-fontSize / 4, -1));
				label.setExcludedFromCamera(this._scene.get('camera', 'camera'), true);
				divComponent.setEnabled(false);
			}
		}
	}

	getCursorPosition() {
		return this._cursorPosition;
	}

	async _eventClicked(id) {
		// Remove the previous data patch, if it exists.
		const earth = this._scene.getEntity('earth');
		const earthSpheroidComponent = earth.getComponentByType('spheroidLOD');
		earth.getComponent('patch').setVisible(false);

		// Fly to event, load event image patch and trigger event description panel here.
		const event = this._content.getEventInfo(id);

		if (event.id === undefined) {
			await this.get('route').navigate('/');
			return;
		}

		let textureUrl = event.image_src;
		textureUrl = textureUrl.replace('$DATAAREA', 'earth');

		const lowerLeft = new Pioneer.LatLonAlt(Pioneer.MathUtils.degToRad(parseFloat(event.south_boundary)), Pioneer.MathUtils.degToRad(parseFloat(event.west_boundary)), 0, true);
		const upperRight = new Pioneer.LatLonAlt(Pioneer.MathUtils.degToRad(parseFloat(event.north_boundary)), Pioneer.MathUtils.degToRad(parseFloat(event.east_boundary)), 0, true);

		// Add the data patch.
		const patchComponent = earth.getComponent('patch');
		let patchSpheroid;
		if (earth.getComponent('patchSpheroid') === null) {
			// console.log("component name",patchComponent.getEntity().getName())
			patchSpheroid = earth.addComponent('spheroid', 'patchSpheroid');
			patchComponent._spheroidComponentRef.setByName(patchComponent.getEntity().getName(), 'patchSpheroid');
		} else {
			patchSpheroid = earth.getComponent('patchSpheroid');
		}

		patchSpheroid.setEquatorialRadius(earth.get('spheroid', 0)._equatorialRadius + 3);
		patchSpheroid.setPolarRadius(earth.get('spheroid', 0)._polarRadius + 3);

		// patchComponent.setRadii(earth.get('spheroid', 0)._equatorialRadius + 3, earth.get('spheroid', 0)._polarRadius + 3);
		patchComponent.setMapping('cylinder');
		patchComponent.setTexture('color', `${textureUrl}`);

		// The patch texture will load if it's put into a test img tag but if it's loaded as above, we get a CORS issue.
		// This happens locally in master as well and may have something to do with a change in Access Control headers on their server.
		// This should work fine in production and we're re-working the event image urls so they point to our servers soon anyway.
		// const testImg = document.createElement('img');
		// testImg.src = textureUrl;
		// document.getElementById('UI')?.appendChild(testImg);

		patchComponent.setBounds(lowerLeft, upperRight);
		patchComponent.setForceLoaded(true);
		patchComponent.forceTextureSize('color', 0);
		patchComponent.setEnabled(true);
		await this._pioneer.waitUntilNextFrame();
		await earthSpheroidComponent.getLoadedPromise();
		await patchComponent.getLoadedPromise();
		patchComponent.setVisible(true);

		// Stop the time rate.
		const previousTimeRate = this.get('time').getTimeRate();
		this.get('time').setTimeRate(0);
		try {
			await this.get('camera').goToEventPatch(event);
		} catch (e) {
			console.warn('goToEventPatch: ', e);
			return;
		}

		// Reset the time rate.
		this.get('time').setTimeRate(previousTimeRate);
	}

	async _poiClicked(id) {
    //Loading image....
		this._loadIconRef.current?.classList.remove('hidden');
		// Remove the previous data patch, if it exists.
		const earth = this._scene.getEntity('earth');
		const earthSpheroidComponent = earth.getComponentByType('spheroid');
		const camera = this._scene.get('camera', 'camera');
		earth.getComponent('patch').setExcludedFromCamera(camera, true);

		// Fly to poi, load poi image patch and trigger poi description panel here.
		const poi = this._content.getPoIInfo(id);
		// console.log("poi", poi)
		if (poi.id === undefined) {
			await this.get('route').navigate('/');
			return;
		}

		 
		// To increase image size in POI because it's too small when registered on the map
		// set to zero for original POI registeration (it is small on the map)
		const valueForBoundary = 0;
		// to move poi image down to the center because it is shifting up from the center I don't know why!
		const northSouthPoiDown = 0.111;
		// seems to be center +/- 4.5316949634 lat, 5.5360521886 lon, width of image seems to not matter
		const lowerLeft = new Pioneer.LatLonAlt(Pioneer.MathUtils.degToRad(parseFloat(poi.south_boundary - northSouthPoiDown - valueForBoundary)),
		 Pioneer.MathUtils.degToRad(parseFloat(poi.west_boundary - valueForBoundary)), 0, true);
		const upperRight = new Pioneer.LatLonAlt(Pioneer.MathUtils.degToRad(parseFloat(poi.north_boundary - northSouthPoiDown + valueForBoundary)),
		 Pioneer.MathUtils.degToRad(parseFloat(poi.east_boundary + valueForBoundary)), 0, true);

		// Add the data patch.
		const patchComponent = earth.getComponent('patch');
		let patchSpheroid;
		if (earth.getComponent('patchSpheroid') === null) {
			patchSpheroid = earth.addComponent('spheroid', 'patchSpheroid');
			patchComponent._spheroidComponentRef.setByName(patchComponent.getEntity().getName(), 'patchSpheroid');
		} else {
			patchSpheroid = earth.getComponent('patchSpheroid');
		}

    let textureUrl
    if(poi.animation_speed){
      textureUrl = poi.images[0];
      window.poiIntervalIdx = 0
      window.poiInterval = setInterval(() => {
        var i = window.poiIntervalIdx + 1 >= poi.images.length ? 0 :  window.poiIntervalIdx + 1
        patchComponent.setTexture('color', `${poi.images[i]}`);
        window.poiIntervalIdx = i

      }, poi.animation_speed)
    } else {
      textureUrl = poi.image_src;
    }
		// reset

		//distance of the image from the earth
		// let sumRadius = 0.1;
		// if(poi.hasOwnProperty("distance_from_poi"))
		//  {
		//  	sumRadius = 2;
		//  }
		patchSpheroid.setEquatorialRadius(earth.get('spheroid', 0)._equatorialRadius + 2);
		patchSpheroid.setPolarRadius(earth.get('spheroid', 0)._polarRadius + 2);

		// patchComponent.setRadii(earth.get('spheroid', 0)._equatorialRadius + 3, earth.get('spheroid', 0)._polarRadius + 3);
		patchComponent.setMapping('cylinder');
		patchComponent.setTexture('color', `${textureUrl}`);
    patchComponent._loadedPromise = e => {console.log("pcload?", patchComponent._loadState)}
		patchComponent.setBounds(lowerLeft, upperRight);
		patchComponent.setForceLoaded(true);
		patchComponent.forceTextureSize('color', 0);
		patchComponent.setEnabled(true);
		await this._pioneer.waitUntilNextFrame();
		await earthSpheroidComponent.getLoadedPromise();
		await patchComponent.getLoadedPromise()

    patchComponent.setResourcesLoadedCallback(() => {
      if(poi.transparent){
        const material = patchComponent._threeJsMaterials[0]
        if(material){
          material.transparent = true
        }
      }
    });
		patchComponent.setExcludedFromCamera(camera, false);
		// Stop the time rate.
		const previousTimeRatePoI = this.get('time').getTimeRate();
		this.get('time').setTimeRate(0);
		await this.get('camera').goToPoIPatch(poi);
		//End Loading image....
		this._loadIconRef.current?.classList.add('hidden');

		// Reset the time rate.
		this.get('time').setTimeRate(previousTimeRatePoI);
	}

	async _gmapClicked(id) {
		//Loading image....
			this._loadIconRef.current?.classList.remove('hidden');
			// Remove the previous data patch, if it exists.
			const earth = this._scene.getEntity('earth');
			const earthSpheroidComponent = earth.getComponentByType('spheroid');
			const camera = this._scene.get('camera', 'camera');
			earth.getComponent('patch').setExcludedFromCamera(camera, true);
	
			// Fly to gmap, load gmap image patch and trigger gmap description panel here.
			const gmap = this._content.getGmapInfo(id);
			// console.log("gmap", gmap)
			if (gmap.id === undefined) {
				await this.get('route').navigate('/');
				return;
			}
	
			 
			// To increase image size in GMAP because it's too small when registered on the map
			// set to zero for original GMAP registeration (it is small on the map)
			const valueForBoundary = 0;
			// to move gmap image down to the center because it is shifting up from the center I don't know why!
			const northSouthGmapDown = 0.111;
			// seems to be center +/- 4.5316949634 lat, 5.5360521886 lon, width of image seems to not matter
			const lowerLeft = new Pioneer.LatLonAlt(Pioneer.MathUtils.degToRad(parseFloat(gmap.south_boundary - northSouthGmapDown - valueForBoundary)),
			 Pioneer.MathUtils.degToRad(parseFloat(gmap.west_boundary - valueForBoundary)), 0, true);
			const upperRight = new Pioneer.LatLonAlt(Pioneer.MathUtils.degToRad(parseFloat(gmap.north_boundary - northSouthGmapDown + valueForBoundary)),
			 Pioneer.MathUtils.degToRad(parseFloat(gmap.east_boundary + valueForBoundary)), 0, true);
	
			// Add the data patch.
			const patchComponent = earth.getComponent('patch');
			let patchSpheroid;
			if (earth.getComponent('patchSpheroid') === null) {
				patchSpheroid = earth.addComponent('spheroid', 'patchSpheroid');
				patchComponent._spheroidComponentRef.setByName(patchComponent.getEntity().getName(), 'patchSpheroid');
			} else {
				patchSpheroid = earth.getComponent('patchSpheroid');
			}
	
		let textureUrl
		if(gmap.animation_speed){
		  textureUrl = gmap.images[0];
		  window.gmapIntervalIdx = 0
		  window.gmapInterval = setInterval(() => {
			var i = window.gmapIntervalIdx + 1 >= gmap.images.length ? 0 :  window.gmapIntervalIdx + 1
			patchComponent.setTexture('color', `${gmap.images[i]}`);
			window.gmapIntervalIdx = i
	
		  }, gmap.animation_speed)
		} else {
		  textureUrl = gmap.image_src;
		}
			patchSpheroid.setEquatorialRadius(earth.get('spheroid', 0)._equatorialRadius + 2);
			patchSpheroid.setPolarRadius(earth.get('spheroid', 0)._polarRadius + 2);
	
			// patchComponent.setRadii(earth.get('spheroid', 0)._equatorialRadius + 3, earth.get('spheroid', 0)._polarRadius + 3);
			patchComponent.setMapping('cylinder');
			patchComponent.setTexture('color', `${textureUrl}`);
		patchComponent._loadedPromise = e => {console.log("pcload?", patchComponent._loadState)}
			patchComponent.setBounds(lowerLeft, upperRight);
			patchComponent.setForceLoaded(true);
			patchComponent.forceTextureSize('color', 0);
			patchComponent.setEnabled(true);
			await this._pioneer.waitUntilNextFrame();
			await earthSpheroidComponent.getLoadedPromise();
			await patchComponent.getLoadedPromise()
	
		patchComponent.setResourcesLoadedCallback(() => {
		  if(gmap.transparent){
			const material = patchComponent._threeJsMaterials[0]
			if(material){
			  material.transparent = true
			}
		  }
		});
			patchComponent.setExcludedFromCamera(camera, false);
			// Stop the time rate.
			const previousTimeRateGmap = this.get('time').getTimeRate();
			this.get('time').setTimeRate(0);
			await this.get('camera').goToGmapPatch(gmap);
			//End Loading image....
			this._loadIconRef.current?.classList.add('hidden');
	
			// Reset the time rate.
			this.get('time').setTimeRate(previousTimeRateGmap);
		}

	// Creates a fullscreen viewport.
	createViewportAndCamera() {
		const viewport = this._pioneer.addViewport();
		viewport.getDiv().style.width = '100%';
		viewport.getDiv().style.height = '100%';
		const cameraEntity = this._scene.addEntity('camera');
		const camera = cameraEntity.addComponent('camera');
		viewport.setCamera(camera);
	}

	// TODO: make standalone function and move to route manager
	goToVitalView(event) {
		const { value, group = 0 } = event.currentTarget.dataset;
    this.get('camera').offsetPioneerViewportLeft(0);
    // console.log("gotoVitalView", event, value)		poiStore.setGlobalState({ showOnGlobePoI: false });
    // Update the global state.
    const _vitalList = this._content.vitalList
    const currentVS = _vitalList.filter((v) => {return v.value === value})
    const ds = currentVS[0]?.datasetGroups[0]?.datasets[0]
		datasetStore.setGlobalState({ currentDataset: { ...ds } });

		const navigated = this.get('route').navigate({

			vitalsign: value,
			altid: group,
			animating: 'f',
			start: '',
			end: '',
			__remove: ['spacecraft', 'altView', 'videoId']
		}, 'vitalsign');

		// In case of no change, router won't navigate
		// Force reset time rate for `satellites`
		if (!navigated && value === 'satellites') {
			this.get('time').setRealTime();
		}
	}

	goToMissionView(event) {
		const { value } = event.currentTarget.dataset;
		const missionId = this.get('content').getMissionId(value);
		this.get('camera').offsetPioneerViewportLeft(0);
		if (!missionId) {
			return;
		}
		eventsStore.setGlobalState({ showOnGlobe: false });
		poiStore.setGlobalState({ showOnGlobePoI: false });
		gmapStore.setGlobalState({ showOnGlobeGmap: false });

		this.get('route').navigate({
			vitalsign: 'satellites',
			spacecraft: missionId,
			__remove: [
				'altid',
				'animating',
				'start',
				'end',
				'videoId',
				'showOnGlobe'
			]
		}, 'spacecraft');
	}

	// TODO: move to route manager
	// goToMissionView(poi) {
	// 	const { value } = poi.currentTarget.dataset;
	// 	const missionId = this.get('content').getMissionId(value);
	// 	if (!missionId) {
	// 		return;
	// 	}

	// 	this.get('route').navigate({
	// 		vitalsign: 'satellites',
	// 		spacecraft: missionId,
	// 		__remove: ['altid', 'animating', 'latest', 'start', 'end']
	// 	}, 'spacecraft');
	// }

	changeAltView(altView) {
		this.get('camera').offsetPioneerViewportLeft(0);
		this.get('video').setVideoQuery(null);
		this.get('route').navigate({
			altView,
			animating: 'f',
			start: '',
			end: '',
			__remove: ['spacecraft, videoId']
		});
	}

	get pioneer() {
		return this._pioneer;
	}

	get scene() {
		return this._scene;
	}

	get(name) {
		if (this._managers[name] !== undefined) {
			return this._managers[name];
		}
		return null;
	}

	/**
	 * Check if resolution is mobile.
	 * NOTE: For most mobile (phone and tablet) portrait, but not tablet landscape or big tablet (e.g. iPad Pro).
	 * Todo: better to define breakpoints as isMobile is misleading - separate functions and event listeners - see ticket #3186
	 */
	isMobile() {
		return window.matchMedia('(max-width: 991.98px)').matches;
	}

	isWindows() {
		return navigator.userAgent.includes('Windows');
	}

	is2k() {
		return window.matchMedia('(min-width: 2100px)').matches;
	}

	is4k() {
		return window.matchMedia('(min-width: 3840px)').matches;
	}

	isLandscape() {
		return window.matchMedia('(orientation: landscape)').matches && window.matchMedia('(max-height: 32em)').matches;
	}

	isTouch() {
		const isTouch = 'ontouchstart' in window || window.navigator.msMaxTouchPoints > 0;
		return isTouch && !this._hasMouse;
	}

	setFullscreen(value) {
		this.setState({ isFullscreen: value });
	}

	render() {
		const {
			sceneIsReady, validQueries, isMobile, isWindows, is2k, is4k, view, viewID, previousView, vitalsign,
			altid, altView, spacecraft, animating, hideUI
		} = this.state;

		if (!sceneIsReady || !validQueries) {
			return <div className='pioneer' ref={this._pioneerRef} />;
		}

		// todo: requires some organizing - it is good to declare any fixed props (anything not affected by state changes) outside of the render function
		// this prevents creating / destructuring lots of new objects in the render loop. See ticket #3186.

		const missionName = this._content.getMissionName(spacecraft);
		const event = this._content.getEventInfo(viewID);
		const poi = this._content.getPoIInfo(viewID);
		const gmap = this._content.getGmapInfo(viewID);
		const isAnimating = animating === 't';
		// not sure how this is done now -RK
		// const { colorBarSrc, colorBarText } = this._content.getColorBarInfo(vsIndex, altid, colorBarIndex);

		// todo: Refactor commonProps into global state - see ticket #3186.
		const commonProps = {
			pioneer: this._pioneer,
			scene: this._scene,
			isMobile,
			isWindows,
			is2k,
			is4k
		};
		const dataProps = {
			view,
			previousView,
			spacecraft,
			vitalsign,
			altid,
			altView,
			missionName,
			event,
			poi,
			gmap
		};
		const vsProps = {
			...commonProps,
			...dataProps,
			vitalSignsListRef: this._content.vitalList,
			eventsListRef: this._content.eventList,
			datasetToolsRef: this._datasetToolsRef,
			goToVitalView: this.goToVitalView,
			ref: this._vitalSignsRef
		};
		const controlsProps = {
			...commonProps,
			view,
			ref: this._controlsRef,
			setFullscreen: this.setFullscreen
		};
		const vitalSignsDetailPanelProps = {
			...dataProps,
			isMobile
		};
		const missionsDetailPanelProps = {
			missionName,
			isMobile
		};
		const eventDetailPanelProps = {
			event,
			isMobile,
			ref: this._detailPanelRef
		};
		const datePickerProps = {
			...commonProps,
			ref: this._datePickerRef
		};
		const datasetToolsProps = {
			...commonProps,
			...dataProps,
			datePickerProps,
			dateLabelRef: this._dateLabelRef,
			datePickerRef: this._datePickerRef,
			ref: this._datasetToolsRef
		};
		const detailPanelProps = {
			...commonProps,
			...dataProps,
			...vitalSignsDetailPanelProps,
			...missionsDetailPanelProps,
			...eventDetailPanelProps,
			// colorBarProps,
			handleSelection: this.goToVitalView,
			type: view,
			detailOverlayRef: this._detailOverlayRef
		};

		const showMission = view === 'spacecraft' || vitalsign === 'satellites';

		const layoutProps = {
			isMobile,
			isWindows,
			hideUI,

			refs: {
				_loadIconRef: this._loadIconRef,
				_datasetToolsRef: this._datasetToolsRef,
				_dateLabelRef: this._dateLabelRef,
				_dateRangeRef: this._dateRangeRef,
				_detailOverlayRef: this._detailOverlayRef
			},

			commonProps,
			dataProps,

			headerProps: {
				view,
				datasetToolsRef: this._datasetToolsRef,
				timeRateControlsRef: this._timeRateControlsRef
				// latestEvent: this._content.eventList[0],
				// latestPoI: this._content.poiList[0]
			},
			// eventProps: {
			// 	allEvents: this._content.eventList,
			// 	allYears: this._content._eventYears
			// },
			// poiProps: {
			// 	allPoI: this._content.poiList,
			// 	allYears: this._content._poiYears
			// },
			vitalsOverlayProps: {
				allVitals: this._content.vitalList,
				allVideos: this._content.videoList
			},
			missionsOverlayProps: {
				allMissions: this._content.missionList
			},
			detailOverlayProps: {
				detailPanelProps,
				datePickerProps,
				goToVitalView: this.goToVitalView,
				changeAltView: this.changeAltView,
				datePickerRef: this._datePickerRef,
				ref: this._detailOverlayRef
			},
			sideMenuProps: {
				language: this.language,
				showMission,
				...(showMission
					? {
						overlay: this._missionsOverlayRef,
						handler: this.goToMissionView,
						selected: missionName
					}
					: {
						overlay: this._datasetsOverlayRef,
						handler: this.goToVitalView,
						selected: vitalsign
					})
			},
			footerProps: {
				dataProps,
				datasetToolsRef: this._datasetToolsRef,
				dateLabelRef: this._dateLabelRef,
				dateRangeRef: this._dateRangeRef,
				view,
				detailPanelProps,
				commonProps,
				datasetToolsProps,
				vsProps,
				controlsProps,
				isMobile,
				timeRateControlsRef: this._timeRateControlsRef
			}
		};

		if (hideUI === true) {
			// Reset city names placemarks
			const placemarks = this.scene.get('placemarks');
			for (let i = 0; i < placemarks.getNumChildren(); i++) {
				placemarks.getChild(i).setEnabled(!hideUI);
			}

			layersStore.setGlobalState({ cityNames: !hideUI });
			eventsStore.setGlobalState({ showOnGlobe: !hideUI });
			poiStore.setGlobalState({ showOnGlobePoI: !hideUI });
			gmapStore.setGlobalState({ showOnGlobeGmap: !hideUI });
		}

		// Do the render.
    const kioskMode = Config.kiosk === true
		return (
			<>
				<div className='pioneer' ref={this._pioneerRef} />
				 <UILayout {...layoutProps} />
				<SplashScreen />
				{kioskMode && playAutoplay && <Autoplay {...commonProps} />}
			</>
		);
	}

	/**
	 * Gets the app version.
	 * @returns {object}
	 */
	getVersion() {
		return AppVersion;
	}
}
export default withRouter(App);
